import type { ReactNode } from 'react';
import { ClockIcon, PhoneIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { StatCard } from '../../billing/StatusCards';

// "Needs attention" and "Overdue items" are deleted outright - their backing
// data (action items) is a cancelled feature and can never populate. Only
// three cards remain: two real+live (All practices, Unresolved calls) and
// one real but display-only (No activity 90d - no backend filter for it yet).

export type PracticeStat = 'all' | 'calls';

interface PracticeStatCardsProps {
    total: number;
    shownCount: number;
    withOpenCalls: number;
    noActivity90d: number;
    activeStat: PracticeStat;
    onChange: (stat: PracticeStat) => void;
}

// A plain, non-interactive stat cell - same visual language as StatCard's
// inactive state (icon/label/value/unit layout, gray-400 accents) but never
// a <button>, so it carries no hover-lift/shadow affordance implying a click.
// StatCard itself stays untouched (its `disabled` placeholder mode is still
// used by other callers) - this is a deliberately separate, smaller component
// for "real data, but nothing to click" rather than "data doesn't exist yet".
function ReadOnlyStatCard({
    label,
    icon,
    value,
    unit
}: {
    label: string;
    icon: ReactNode;
    value: string;
    unit: string;
}) {
    return (
        <div className="flex-1 min-w-0 flex flex-col justify-center px-5 py-3.5 rounded-xl border-[1.5px] border-gray-200 bg-white shadow-sm text-left">
            <div className="flex items-center gap-2 mb-1">
                <span className="flex-shrink-0 text-gray-400">{icon}</span>
                <span className="text-[11px] font-semibold tracking-[0.04em] uppercase text-gray-400">
                    {label}
                </span>
            </div>
            <div className="flex items-baseline gap-1.5 pl-[22px] min-w-0">
                <span className="text-[26px] font-bold leading-none tabular-nums flex-shrink-0 text-gray-800">
                    {value}
                </span>
                <span className="text-xs font-medium truncate text-gray-400">{unit}</span>
            </div>
        </div>
    );
}

export function PracticeStatCards({
    total,
    shownCount,
    withOpenCalls,
    noActivity90d,
    activeStat,
    onChange
}: PracticeStatCardsProps) {
    return (
        <div className="grid grid-cols-3 gap-3 shrink-0">
            <StatCard
                label="All practices"
                icon={<Squares2X2Icon className="h-3.5 w-3.5 text-gray-400" />}
                value={String(total)}
                unit={`${shownCount} shown`}
                isActive={activeStat === 'all'}
                onClick={() => onChange('all')}
            />
            <StatCard
                label="Unresolved calls"
                icon={<PhoneIcon className="h-3.5 w-3.5" />}
                value={String(withOpenCalls)}
                unit="unresolved"
                isActive={activeStat === 'calls'}
                onClick={() => onChange('calls')}
            />
            <ReadOnlyStatCard
                label="No activity 90d"
                icon={<ClockIcon className="h-3.5 w-3.5" />}
                value={String(noActivity90d)}
                unit="quiet practices"
            />
        </div>
    );
}
